import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Link } from 'react-router-dom';
import { Dropdown, message, Pagination } from 'antd';
import { apiService } from '../../services/api';
import './unavailable-modems-block.css';

/**
 * UnavailableModemsBlock — блок "Недоступные модемы"
 * Встроенная сортировка + фильтрация по столбцам
 */

const SORT_FIELD_MAP = {
  id: 'id',
  server: 'server',
  geo: 'geo',
  modem: 'modemName',
  load: 'loadPercentage',
  proxy: 'proxyCount',
  signal: 'signalLevel',
  operator: 'operator',
  reconnect: 'reconnectType',
};

const FILTER_FIELD_MAP = {
  id: 'id',
  server: 'server',
  geo: 'geo',
  modem: 'modemName',
  status: 'status',
  load: 'loadType',
  operator: 'operator',
  reconnect: 'reconnectType',
};

const FILTER_DROPDOWN_ESTIMATED_WIDTH = 280;
const FILTER_DROPDOWN_ESTIMATED_HEIGHT = 340;
const FILTER_DROPDOWN_VIEWPORT_MARGIN = 8;

const UMB_PAGE_SIZE = 20;

const SortIcon = ({ active = false, direction = 'asc' }) => (
  <svg
    className={`umb__sort-icon ${active ? 'umb__sort-icon--active' : ''} ${direction === 'desc' ? 'umb__sort-icon--desc' : ''}`}
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M6 9L12 15L18 9"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const FilterIcon = ({ active = false }) => (
  <svg
    className={`umb__filter-icon ${active ? 'umb__filter-icon--active' : ''}`}
    width="15"
    height="15"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M9.00005 14C8.89187 14 8.7866 13.9649 8.70005 13.9L6.70005 12.4C6.63795 12.3534 6.58755 12.293 6.55284 12.2236C6.51812 12.1542 6.50005 12.0776 6.50005 12V9.19L2.99205 5.2435C2.74291 4.96244 2.58024 4.61541 2.52359 4.24412C2.46694 3.87283 2.51872 3.49308 2.67271 3.15051C2.8267 2.80794 3.07635 2.51713 3.39164 2.31303C3.70694 2.10894 4.07446 2.00024 4.45005 2H11.5501C11.9256 2.00044 12.293 2.10932 12.6082 2.31355C12.9234 2.51779 13.1729 2.80869 13.3267 3.15129C13.4805 3.4939 13.5321 3.87364 13.4753 4.24487C13.4185 4.61611 13.2558 4.96305 13.0066 5.244L9.50005 9.19V13.5C9.50005 13.6326 9.44737 13.7598 9.3536 13.8536C9.25984 13.9473 9.13266 14 9.00005 14ZM7.50005 11.75L8.50005 12.5V9C8.50015 8.87758 8.54516 8.75945 8.62655 8.668L12.2606 4.5795C12.3817 4.44254 12.4608 4.27352 12.4883 4.09273C12.5157 3.91193 12.4905 3.72705 12.4154 3.56028C12.3404 3.39351 12.2189 3.25193 12.0654 3.15254C11.9118 3.05316 11.7329 3.00019 11.5501 3H4.45005C4.26728 3.00028 4.08846 3.05327 3.93505 3.15263C3.78164 3.25199 3.66015 3.3935 3.58516 3.56018C3.51016 3.72685 3.48483 3.91163 3.51221 4.09234C3.5396 4.27305 3.61854 4.44203 3.73955 4.579L7.37405 8.668C7.45526 8.75952 7.50009 8.87764 7.50005 9V11.75Z"
      fill="currentColor" stroke="currentColor" strokeWidth="0.6"
    />
  </svg>
);

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const RebootIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M20 11A8 8 0 108 18.93" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M20 4V11H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const MenuDotsIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="12" cy="4" r="2" fill="currentColor" />
    <circle cx="12" cy="12" r="2" fill="currentColor" />
    <circle cx="12" cy="20" r="2" fill="currentColor" />
  </svg>
);

const ArrowRightIcon = () => (
  <svg
    className="umb__btn-icon"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M9 6L15 12L9 18"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const BugIcon = () => (
  <svg
    className="umb__bug-icon"
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <g clipPath="url(#umb-bug-clip)">
      <path d="M11 3.5C11.2652 3.5 11.5196 3.39464 11.7071 3.20711C11.8946 3.01957 12 2.76522 12 2.5V0.5C12 0.367392 11.9473 0.240215 11.8536 0.146447C11.7598 0.0526784 11.6326 0 11.5 0C11.3674 0 11.2402 0.0526784 11.1464 0.146447C11.0527 0.240215 11 0.367392 11 0.5V2.5H9C8.991 2.5 8.984 2.5045 8.975 2.505C8.73585 1.9125 8.32509 1.40499 7.79545 1.04761C7.2658 0.690226 6.64144 0.499281 6.0025 0.499281C5.36356 0.499281 4.7392 0.690226 4.20955 1.04761C3.67991 1.40499 3.26915 1.9125 3.03 2.505C3.0195 2.5055 3.0105 2.5 3 2.5H1V0.5C1 0.367392 0.947322 0.240215 0.853553 0.146447C0.759785 0.0526784 0.632608 0 0.5 0C0.367392 0 0.240215 0.0526784 0.146447 0.146447C0.0526784 0.240215 0 0.367392 0 0.5L0 2.5C0 2.76522 0.105357 3.01957 0.292893 3.20711C0.48043 3.39464 0.734784 3.5 1 3.5H2.7425L2.236 5.5H0.5C0.367392 5.5 0.240215 5.55268 0.146447 5.64645C0.0526784 5.74021 0 5.86739 0 6C0 6.13261 0.0526784 6.25979 0.146447 6.35355C0.240215 6.44732 0.367392 6.5 0.5 6.5H1.9825L1.91 6.7865L1.9 6.85C1.87771 7.02111 1.86602 7.19344 1.865 7.366C1.86551 7.74969 1.91987 8.13142 2.0265 8.5H1C0.734784 8.5 0.48043 8.60536 0.292893 8.79289C0.105357 8.98043 0 9.23478 0 9.5L0 11.5C0 11.6326 0.0526784 11.7598 0.146447 11.8536C0.240215 11.9473 0.367392 12 0.5 12C0.632608 12 0.759785 11.9473 0.853553 11.8536C0.947322 11.7598 1 11.6326 1 11.5V9.5H2.465C2.83155 10.1101 3.34976 10.6149 3.96921 10.9654C4.58866 11.3159 5.28827 11.5001 6 11.5001C6.71173 11.5001 7.41134 11.3159 8.03079 10.9654C8.65024 10.6149 9.16845 10.1101 9.535 9.5H11V11.5C11 11.6326 11.0527 11.7598 11.1464 11.8536C11.2402 11.9473 11.3674 12 11.5 12C11.6326 12 11.7598 11.9473 11.8536 11.8536C11.9473 11.7598 12 11.6326 12 11.5V9.5C12 9.23478 11.8946 8.98043 11.7071 8.79289C11.5196 8.60536 11.2652 8.5 11 8.5H9.9735C10.0807 8.13066 10.1354 7.74808 10.136 7.3635C10.1345 7.19176 10.1225 7.02027 10.1 6.85L10.0125 6.5H11.5C11.6326 6.5 11.7598 6.44732 11.8536 6.35355C11.9473 6.25979 12 6.13261 12 6C12 5.86739 11.9473 5.74021 11.8536 5.64645C11.7598 5.55268 11.6326 5.5 11.5 5.5H9.761L9.2565 3.5H11ZM6 10.5C5.16848 10.4991 4.37129 10.1683 3.78336 9.58029C3.19543 8.99227 2.86479 8.19502 2.864 7.3635C2.86469 7.24198 2.87253 7.1206 2.8875 7L3.9025 2.985C4.05483 2.55255 4.33709 2.17775 4.71065 1.91191C5.0842 1.64606 5.53079 1.50217 5.98928 1.49992C6.44777 1.49768 6.89575 1.63719 7.27189 1.89937C7.64803 2.16154 7.93394 2.53356 8.0905 2.9645L9.1125 7C9.12743 7.12027 9.13527 7.24131 9.136 7.3625C9.13547 8.19419 8.80495 8.9917 8.21699 9.57993C7.62904 10.1682 6.83169 10.4991 6 10.5Z" fill="currentColor"/>
    </g>
    <defs>
      <clipPath id="umb-bug-clip">
        <rect width="12" height="12" fill="white"/>
      </clipPath>
    </defs>
  </svg>
);

const SignalBars = ({ level = 0, maxLevel = 5 }) => {
  const bars = [];
  for (let i = 0; i < maxLevel; i++) {
    bars.push(
      <span
        key={i}
        className={`umb__signal-bar ${i < level ? 'umb__signal-bar--active' : ''}`}
      />
    );
  }
  return (
    <div className="umb__signal-bars">
      {bars}
      <span className="umb__signal-text">{level}/{maxLevel}</span>
    </div>
  );
};

const LoadProgress = ({ type = 'min', percentage = 0 }) => {
  const typeClass = type.toLowerCase();
  return (
    <div className="umb__load-progress">
      <span className={`umb__load-type umb__load-type--${typeClass}`}>
        {type}
      </span>
      <span className="umb__load-value">{percentage}%</span>
    </div>
  );
};

const OperatorBadge = ({ operator }) => {
  const operatorLower = (operator || '').toLowerCase();
  let badgeClass = 'umb__operator-badge';

  if (operatorLower.includes('мтс') || operatorLower === 'mts') {
    badgeClass += ' umb__operator-badge--mts';
  } else if (operatorLower.includes('т2') || operatorLower === 't2' || operatorLower.includes('tele2')) {
    badgeClass += ' umb__operator-badge--t2';
  } else if (operatorLower.includes('билайн') || operatorLower === 'beeline') {
    badgeClass += ' umb__operator-badge--beeline';
  } else if (operatorLower.includes('мегафон') || operatorLower === 'megafon') {
    badgeClass += ' umb__operator-badge--megafon';
  }

  return <span className={badgeClass}>{operator}</span>;
};

const ColumnHeader = ({
  label,
  columnKey,
  sortable = false,
  filterable = false,
  sortConfig,
  onSort,
  onFilter,
  filterActive = false,
}) => {
  const isActive = sortConfig?.key === columnKey;
  const direction = isActive ? sortConfig.direction : 'asc';

  const handleSortClick = () => {
    if (sortable && onSort) {
      onSort(columnKey);
    }
  };

  const handleFilterClick = (e) => {
    e.stopPropagation();
    if (filterable && onFilter) {
      onFilter(columnKey, e, label);
    }
  };

  return (
    <div className="umb__header-content">
      <span className="umb__header-label">{label}</span>
      {sortable && (
        <button
          type="button"
          className="umb__header-btn"
          onClick={handleSortClick}
          aria-label={`Сортировать по ${label}`}
          tabIndex={0}
        >
          <SortIcon active={isActive} direction={direction} />
        </button>
      )}
      {filterable && (
        <button
          type="button"
          className={`umb__header-btn ${filterActive ? 'umb__header-btn--active' : ''}`}
          onClick={handleFilterClick}
          aria-label={`Фильтр по ${label}`}
          tabIndex={0}
        >
          <FilterIcon active={filterActive} />
        </button>
      )}
    </div>
  );
};

const FilterDropdown = ({ columnKey, title, modems, filters, onFilterChange, position, onClose }) => {
  const dropdownRef = useRef(null);
  const [search, setSearch] = useState('');
  const committedValues = filters[columnKey] || new Set();
  const [draft, setDraft] = useState(() => new Set(committedValues));

  useEffect(() => {
    setDraft(new Set(filters[columnKey] || new Set()));
  }, [filters, columnKey]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const fieldKey = FILTER_FIELD_MAP[columnKey];
  const uniqueValues = useMemo(() => {
    const vals = new Set();
    modems.forEach((m) => {
      const v = m[fieldKey];
      if (v != null && String(v) !== '') vals.add(String(v));
    });
    return [...vals].sort((a, b) => a.localeCompare(b, 'ru'));
  }, [modems, fieldKey]);

  const filtered = useMemo(() => {
    if (!search) return uniqueValues;
    const q = search.toLowerCase();
    return uniqueValues.filter((v) => v.toLowerCase().includes(q));
  }, [uniqueValues, search]);

  const handleToggle = (value) => {
    setDraft((prev) => {
      const next = new Set(prev);
      if (next.has(value)) {
        next.delete(value);
      } else {
        next.add(value);
      }
      return next;
    });
  };

  const handleReset = () => {
    setDraft(new Set());
    onFilterChange(columnKey, new Set());
    onClose();
  };

  const handleApply = () => {
    onFilterChange(columnKey, draft);
    onClose();
  };

  return ReactDOM.createPortal(
    <div
      ref={dropdownRef}
      className="umb__filter-dropdown"
      style={{ top: position.top, left: position.left }}
    >
      <div className="umb__filter-dropdown-header">Фильтр: {title || columnKey}</div>
      <div className="umb__filter-dropdown-search">
        <input
          className="umb__filter-dropdown-search-input"
          placeholder="Поиск..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          autoFocus
        />
      </div>
      <div className="umb__filter-list">
        {filtered.length === 0 && (
          <div className="umb__filter-dropdown-empty">Ничего не найдено</div>
        )}
        {filtered.map((val) => (
          <label key={val} className="umb__filter-option">
            <input
              type="checkbox"
              className="umb__filter-checkbox"
              checked={draft.has(val)}
              onChange={() => handleToggle(val)}
            />
            <span className="umb__filter-option-label">{val || '—'}</span>
          </label>
        ))}
      </div>
      <div className="umb__filter-actions">
        <button
          type="button"
          className="umb__filter-action-btn umb__filter-action-btn--reset"
          onClick={handleReset}
        >
          Сбросить
        </button>
        <button
          type="button"
          className="umb__filter-action-btn umb__filter-action-btn--apply"
          onClick={handleApply}
        >
          Применить
        </button>
      </div>
    </div>,
    document.body
  );
};

const UnavailableModemsBlock = ({
  title = 'Недоступные модемы',
  linkTo = '/modems',
  modems = [],
  onSort,
  onFilter,
  onActionClick,
  onRefresh,
  onRebootModem,
  onRebootAll,
  bulkRebootLoading = false,
  isRebootDisabled,
}) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [filters, setFilters] = useState({});
  const [activeFilterColumn, setActiveFilterColumn] = useState(null);
  const [activeFilterTitle, setActiveFilterTitle] = useState('');
  const [filterPosition, setFilterPosition] = useState({ top: 0, left: 0 });
  const [bulkCheckLoading, setBulkCheckLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const columns = [
    { key: 'server', label: 'Сервер', sortable: true, filterable: true, width: 140 },
    { key: 'geo', label: 'ГЕО', sortable: true, filterable: true, width: 140 },
    { key: 'modem', label: 'Модем', sortable: true, filterable: true, width: 140 },
    { key: 'status', label: 'Статус', sortable: false, filterable: true, width: 140 },
    { key: 'load', label: 'Общая загрузка', sortable: true, filterable: true, width: 140 },
    { key: 'proxy', label: 'Прокси', sortable: true, filterable: false, width: 90 },
    { key: 'signal', label: 'Уровень сигнала', sortable: true, filterable: false, width: 140 },
    { key: 'internet', label: 'Интернет', sortable: false, filterable: false, width: 120 },
    { key: 'operator', label: 'Оператор', sortable: true, filterable: true, width: 140 },
    { key: 'performance', label: 'Производительность', sortable: false, filterable: false, width: 'flex' },
    { key: 'reconnect', label: 'Переподключение', sortable: true, filterable: true, width: 140 },
  ];

  const handleSort = useCallback((columnKey) => {
    setSortConfig((prev) => {
      const newDirection = prev.key === columnKey && prev.direction === 'asc' ? 'desc' : 'asc';
      const newConfig = { key: columnKey, direction: newDirection };
      if (onSort) onSort(newConfig);
      return newConfig;
    });
  }, [onSort]);

  const handleFilter = useCallback((columnKey, event, colTitle) => {
    if (activeFilterColumn === columnKey) {
      setActiveFilterColumn(null);
      return;
    }

    const getSafeFilterPosition = (triggerRect) => {
      const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
      const maxLeft = Math.max(
        FILTER_DROPDOWN_VIEWPORT_MARGIN,
        viewportWidth - FILTER_DROPDOWN_ESTIMATED_WIDTH - FILTER_DROPDOWN_VIEWPORT_MARGIN
      );
      const preferredLeft = triggerRect.left;
      const safeLeft = Math.min(Math.max(preferredLeft, FILTER_DROPDOWN_VIEWPORT_MARGIN), maxLeft);

      const preferredTop = triggerRect.bottom + 4;
      const topIfOpenedUp = triggerRect.top - FILTER_DROPDOWN_ESTIMATED_HEIGHT - 4;
      const wouldOverflowBottom = preferredTop + FILTER_DROPDOWN_ESTIMATED_HEIGHT > viewportHeight - FILTER_DROPDOWN_VIEWPORT_MARGIN;
      const fallbackTop = Math.max(FILTER_DROPDOWN_VIEWPORT_MARGIN, topIfOpenedUp);
      const safeTop = wouldOverflowBottom ? fallbackTop : preferredTop;

      return { top: safeTop, left: safeLeft };
    };

    setActiveFilterColumn(columnKey);
    setActiveFilterTitle(colTitle || columnKey);
    const rect = event.currentTarget.getBoundingClientRect();
    setFilterPosition(getSafeFilterPosition(rect));
    if (onFilter) onFilter(columnKey);
  }, [onFilter, activeFilterColumn]);

  const handleFilterChange = useCallback((columnKey, selectedValues) => {
    setFilters((prev) => {
      const next = { ...prev };
      if (selectedValues.size === 0) {
        delete next[columnKey];
      } else {
        next[columnKey] = selectedValues;
      }
      return next;
    });
  }, []);

  const handleCloseFilter = useCallback(() => {
    setActiveFilterColumn(null);
  }, []);

  const handleClearFilters = useCallback(() => {
    setFilters({});
    setActiveFilterColumn(null);
  }, []);

  const processedModems = useMemo(() => {
    let result = modems;

    const activeFilters = Object.entries(filters);
    if (activeFilters.length > 0) {
      result = result.filter((modem) =>
        activeFilters.every(([key, selectedValues]) => {
          if (selectedValues.size === 0) return true;
          const fieldKey = FILTER_FIELD_MAP[key];
          const value = String(modem[fieldKey] ?? '');
          return selectedValues.has(value);
        })
      );
    }

    if (sortConfig.key) {
      const fieldKey = SORT_FIELD_MAP[sortConfig.key];
      if (fieldKey) {
        result = [...result].sort((a, b) => {
          const aVal = a[fieldKey];
          const bVal = b[fieldKey];
          if (aVal == null && bVal == null) return 0;
          if (aVal == null) return 1;
          if (bVal == null) return -1;
          if (typeof aVal === 'number' && typeof bVal === 'number') {
            return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
          }
          const cmp = String(aVal).localeCompare(String(bVal), 'ru');
          return sortConfig.direction === 'asc' ? cmp : -cmp;
        });
      }
    }

    return result;
  }, [modems, filters, sortConfig]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(processedModems.length / UMB_PAGE_SIZE));
    setCurrentPage((p) => Math.min(p, maxPage));
  }, [processedModems.length]);

  const paginatedModems = useMemo(() => {
    const start = (currentPage - 1) * UMB_PAGE_SIZE;
    return processedModems.slice(start, start + UMB_PAGE_SIZE);
  }, [processedModems, currentPage]);

  const hasActiveFilters = Object.keys(filters).length > 0;

  const handleCheckUnavailableModems = useCallback(async () => {
    const modemIds = Array.from(
      new Set(
        processedModems
          .map((modem) => Number(modem?.modem_id || 0))
          .filter((modemId) => modemId > 0)
      )
    );
    if (modemIds.length === 0) {
      message.warning('В таблице нет модемов для проверки');
      return;
    }

    setBulkCheckLoading(true);
    message.loading({
      key: 'umb-bulk-check',
      content: `Проверка прокси на ${modemIds.length} модемах...`,
      duration: 0,
    });

    let totalChecked = 0;
    let totalAvailable = 0;
    let totalUnavailable = 0;
    let totalErrors = 0;

    try {
      for (const modemId of modemIds) {
        try {
          const response = await apiService.quickCheckModemProxies(modemId);
          const payload = response?.data?.data || response?.data || {};
          totalChecked += Number(payload?.total_checked || 0);
          totalAvailable += Number(payload?.available || 0);
          totalUnavailable += Number(payload?.unavailable || 0);
          totalErrors += Number(payload?.errors || 0);
        } catch {
          totalErrors += 1;
        }
      }

      message.destroy('umb-bulk-check');
      if (totalChecked === 0) {
        message.warning('Проверка завершена: активных прокси на выбранных модемах не найдено');
      } else {
        message.success(
          `Проверка завершена: доступно ${totalAvailable}, недоступно ${totalUnavailable}, ошибок ${totalErrors}`
        );
      }

      if (onRefresh) {
        onRefresh();
      }
    } finally {
      setBulkCheckLoading(false);
      message.destroy('umb-bulk-check');
    }
  }, [onRefresh, processedModems]);

  const filterTags = useMemo(() => {
    const tags = [];
    for (const [key, values] of Object.entries(filters)) {
      const col = columns.find((c) => c.key === key);
      const colLabel = col?.label || key;
      values.forEach((v) => tags.push({ columnKey: key, value: v, label: `${colLabel}: ${v}` }));
    }
    return tags;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  return (
    <div className="umb">
      {/* Title */}
      <div className="umb__title-row">
        <h3 className="umb__title">{title}</h3>
        <button
          type="button"
          className={`umb__bug-btn ${bulkRebootLoading ? 'umb__bug-btn--loading' : ''}`}
          onClick={onRebootAll}
          disabled={bulkRebootLoading || processedModems.length === 0 || typeof onRebootAll !== 'function'}
          aria-label="Перезагрузить недоступные модемы из таблицы"
          tabIndex={0}
        >
          <RebootIcon />
        </button>
        <button
          type="button"
          className={`umb__bug-btn ${bulkCheckLoading ? 'umb__bug-btn--loading' : ''}`}
          onClick={handleCheckUnavailableModems}
          disabled={bulkCheckLoading || processedModems.length === 0}
          aria-label="Проверить нерабочие прокси на модемах из таблицы"
          tabIndex={0}
        >
          <BugIcon />
        </button>
        <Link to={linkTo} className="umb__btn">
          <span>В раздел</span>
          <ArrowRightIcon />
        </Link>
      </div>

      {/* Active filter tags */}
      {hasActiveFilters && (
        <div className="umb__active-filters">
          {filterTags.map((tag) => (
            <button
              key={`${tag.columnKey}-${tag.value}`}
              type="button"
              className="umb__filter-tag"
              onClick={() => {
                const newSet = new Set(filters[tag.columnKey]);
                newSet.delete(tag.value);
                handleFilterChange(tag.columnKey, newSet);
              }}
              aria-label={`Убрать фильтр ${tag.label}`}
              tabIndex={0}
            >
              {tag.label} ✕
            </button>
          ))}
          <button
            type="button"
            className="umb__filter-clear"
            onClick={handleClearFilters}
            tabIndex={0}
          >
            Сбросить все
          </button>
        </div>
      )}

      {/* Table */}
      <div className="umb__table-wrap">
        <table className="umb__table">
          <thead>
            <tr className="umb__row umb__row--header">
              <th className="umb__cell umb__cell--header umb__cell--actions">
                <MenuDotsIcon />
              </th>
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={`umb__cell umb__cell--header umb__cell--${col.key}`}
                  style={col.width !== 'flex' ? { width: col.width, minWidth: col.width } : {}}
                >
                  <ColumnHeader
                    label={col.label}
                    columnKey={col.key}
                    sortable={col.sortable}
                    filterable={col.filterable}
                    sortConfig={sortConfig}
                    onSort={handleSort}
                    onFilter={handleFilter}
                    filterActive={!!filters[col.key]}
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginatedModems.map((modem, index) => (
              <tr key={modem.id || `${currentPage}-${index}`} className="umb__row">
                <td className="umb__cell umb__cell--actions">
                  <Dropdown
                    overlayClassName="pm-action-dropdown"
                    menu={{
                      items: [
                        {
                          key: 'reboot',
                          icon: <RebootIcon />,
                          label: 'Перезагрузка',
                          disabled: typeof isRebootDisabled === 'function' ? Boolean(isRebootDisabled(modem)) : false,
                          onClick: () => {
                            if (typeof onRebootModem === 'function') {
                              onRebootModem(modem);
                            }
                          }
                        },
                        {
                          key: 'check',
                          icon: <CheckIcon />,
                          label: 'Проверить прокси',
                          onClick: async () => {
                            const serverId = modem.modem_id;
                            if (!serverId) {
                              message.error('Не удалось определить ID модема');
                              return;
                            }
                            const name = modem.modemName || `#${serverId}`;
                            message.loading({ content: `Проверка ${name}...`, key: `umb-check-${serverId}`, duration: 0 });
                            try {
                              const res = await apiService.quickCheckModemProxies(serverId);
                              const d = res.data?.data || res.data || {};
                              message.destroy(`umb-check-${serverId}`);
                              if (d.total_checked === 0) {
                                message.warning(`${name}: нет активных прокси`);
                              } else {
                                message.success(`${name}: доступно ${d.available}, недоступно ${d.unavailable}${d.errors ? `, ошибок ${d.errors}` : ''}`);
                                if (d.available > 0 && onRefresh) onRefresh();
                              }
                            } catch (err) {
                              message.destroy(`umb-check-${serverId}`);
                              message.error(`Ошибка: ${err.response?.data?.detail || err.message}`);
                            }
                          }
                        }
                      ]
                    }}
                    trigger={['click']}
                    placement="bottomRight"
                  >
                    <button
                      type="button"
                      className="umb__action-btn"
                      aria-label="Действия"
                      tabIndex={0}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <MenuDotsIcon />
                    </button>
                  </Dropdown>
                </td>
                <td className="umb__cell umb__cell--server umb__cell--muted">{modem.server}</td>
                <td className="umb__cell umb__cell--geo umb__cell--muted">{modem.geo}</td>
                <td className="umb__cell umb__cell--modem umb__cell--muted">{modem.modemName}</td>
                <td className="umb__cell umb__cell--status">
                  <span className="umb__badge umb__badge--red">{modem.status || 'Недоступен'}</span>
                </td>
                <td className="umb__cell umb__cell--load">
                  <LoadProgress type={modem.loadType || 'Min'} percentage={modem.loadPercentage || 0} />
                </td>
                <td className="umb__cell umb__cell--proxy umb__cell--muted">{modem.proxyCount ?? 1}</td>
                <td className="umb__cell umb__cell--signal">
                  <SignalBars level={modem.signalLevel ?? 0} maxLevel={5} />
                </td>
                <td className="umb__cell umb__cell--internet">
                  <span className={`umb__badge ${modem.internetStatus === 'Подключен' ? 'umb__badge--green' : 'umb__badge--grey'}`}>
                    {modem.internetStatus || 'Отключен'}
                  </span>
                </td>
                <td className="umb__cell umb__cell--operator">
                  <OperatorBadge operator={modem.operator} />
                </td>
                <td className="umb__cell umb__cell--performance">
                  <span className="umb__performance-value">{modem.performance || '—'}</span>
                </td>
                <td className="umb__cell umb__cell--reconnect">
                  {modem.reconnectType && (
                    <span className={`umb__badge ${
                      modem.reconnectType === 'link' ? 'umb__badge--blue' :
                      modem.reconnectType === 'time' ? 'umb__badge--purple' :
                      modem.reconnectType === 'time_link' ? 'umb__badge--yellow' : 'umb__badge--gray'
                    }`}>
                      {modem.reconnectType === 'link' ? 'Ссылка' : modem.reconnectType === 'time' ? 'Время' : modem.reconnectType === 'time_link' ? 'Время + ссылка' : modem.reconnectType}
                    </span>
                  )}
                  <span className="umb__reconnect-time">{modem.reconnectTime || '—'}</span>
                </td>
              </tr>
            ))}
            {processedModems.length === 0 && (
              <tr className="umb__row umb__row--empty">
                <td colSpan={12} className="umb__cell umb__cell--empty">
                  {hasActiveFilters ? 'Нет модемов по выбранным фильтрам' : 'Нет недоступных модемов'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {processedModems.length > 0 ? (
        <div className="umb__pagination-wrap">
          <Pagination
            size="small"
            current={currentPage}
            pageSize={UMB_PAGE_SIZE}
            total={processedModems.length}
            showSizeChanger={false}
            onChange={(page) => setCurrentPage(page)}
            showTotal={(total, range) => `${range[0]}–${range[1]} из ${total}`}
          />
        </div>
      ) : null}

      {/* Filter dropdown (portal) */}
      {activeFilterColumn && (
        <FilterDropdown
          columnKey={activeFilterColumn}
          title={activeFilterTitle}
          modems={modems}
          filters={filters}
          onFilterChange={handleFilterChange}
          position={filterPosition}
          onClose={handleCloseFilter}
        />
      )}
    </div>
  );
};

export default UnavailableModemsBlock;
